import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ClientOverviewTable = ({ clients, selectedClients, onSelectClient, onClientAction }) => {
  const [sortField, setSortField] = useState('companyName');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedClients = [...clients]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];

    if (typeof aVal === 'string') {
      aVal = aVal?.toLowerCase();
      bVal = bVal?.toLowerCase();
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getSetupProgressColor = (progress) => {
    if (progress === 100) return { color: 'bg-success', label: 'Complete' };
    if (progress >= 50) return { color: 'bg-warning', label: 'In Progress' };
    return { color: 'bg-error', label: 'Incomplete' };
  };

  const getComplianceColor = (score) => {
    if (score >= 90) return { label: 'Excellent', color: 'text-success', bg: 'bg-success/10' };
    if (score >= 70) return { label: 'Good', color: 'text-warning', bg: 'bg-warning/10' };
    return { label: 'Warning', color: 'text-error', bg: 'bg-error/10' };
  };

  const getBillingStatus = (nextBillingDate, subscriptionStatus) => {
    if (subscriptionStatus === 'suspended') {
      return { label: 'Suspended', color: 'text-error', bg: 'bg-error/10' };
    }
    if (!nextBillingDate) return { label: 'Overdue', color: 'text-error', bg: 'bg-error/10' };
    
    const daysUntilBilling = Math.floor(
      (new Date(nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilBilling < 0) return { label: 'Overdue', color: 'text-error', bg: 'bg-error/10' };
    if (daysUntilBilling <= 7) return { label: 'Due Soon', color: 'text-warning', bg: 'bg-warning/10' };
    return { label: 'Current', color: 'text-success', bg: 'bg-success/10' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const SortableHeader = ({ field, label }) => (
    <th
      className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {label}
        {sortField === field && (
          <Icon
            name={sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
            size={14}
          />
        )}
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-4 w-12">
              <Checkbox />
            </th>
            <SortableHeader field="companyName" label="Organization Name" />
            <SortableHeader field="employeeCount" label="Employees" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Setup Progress</th>
            <SortableHeader field="complianceScore" label="Compliance Score" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Billing Status</th>
            <SortableHeader field="lastActivityDate" label="Last Activity" />
            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Quick Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedClients?.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center py-8 text-muted-foreground">
                No client organizations found
              </td>
            </tr>
          ) : (
            sortedClients?.map((client) => {
              const setupProgress = getSetupProgressColor(client?.setupProgress || 0);
              const complianceStatus = getComplianceColor(client?.complianceScore || 0);
              const billingStatus = getBillingStatus(client?.nextBillingDate, client?.subscriptionStatus);
              const isSelected = selectedClients?.includes(client?.id);

              return (
                <tr key={client?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-3 px-4">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onSelectClient?.(client?.id)}
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{client?.companyName}</p>
                      <p className="text-xs text-muted-foreground">{client?.contactEmail}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {client?.employeeCount || 0}
                  </td>
                  <td className="py-3 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full ${setupProgress?.color} transition-all`}
                            style={{ width: `${client?.setupProgress || 0}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-foreground w-12 text-right">
                          {client?.setupProgress || 0}%
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{setupProgress?.label}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-bold ${complianceStatus?.color}`}>
                        {client?.complianceScore || 0}%
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${complianceStatus?.bg} ${complianceStatus?.color}`}>
                        {complianceStatus?.label}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${billingStatus?.bg} ${billingStatus?.color}`}>
                        {billingStatus?.label}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        Next: {formatDate(client?.nextBillingDate)}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-foreground">
                    {formatDate(client?.lastActivityDate)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onClientAction?.(client, 'access')}
                        title="Direct account access"
                      >
                        <Icon name="LogIn" size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onClientAction?.(client, 'billing')}
                        title="Billing adjustments"
                      >
                        <Icon name="CreditCard" size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onClientAction?.(client, 'support')}
                        title="Create support ticket"
                      >
                        <Icon name="Headphones" size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onClientAction?.(client, 'suspend')}
                        title="Emergency account suspension"
                      >
                        <Icon name="Ban" size={14} className="text-error" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ClientOverviewTable;